import { useCallback, useEffect, useRef, useState } from 'react'

import { MAX_CONSECUTIVE_FAILURES } from '../constants'

export interface ConsecutiveFailuresState {
  consecutiveFailures: number
  shouldStopPolling: boolean
  recordSuccess: () => void
  recordFailure: () => void
  reset: () => void
}

export interface UseConsecutiveFailuresOptions {
  maxFailures?: number
  onPollingStop?: () => void
}

export const useConsecutiveFailures = ({
  maxFailures = MAX_CONSECUTIVE_FAILURES,
  onPollingStop,
}: UseConsecutiveFailuresOptions = {}): ConsecutiveFailuresState => {
  const [consecutiveFailures, setConsecutiveFailures] = useState(0)
  const onPollingStopRef = useRef(onPollingStop)

  useEffect(() => {
    onPollingStopRef.current = onPollingStop
  }, [onPollingStop])

  const shouldStopPolling = consecutiveFailures >= maxFailures

  useEffect(() => {
    if (shouldStopPolling && onPollingStopRef.current) {
      onPollingStopRef.current()
    }
  }, [shouldStopPolling])

  const recordSuccess = useCallback(() => {
    setConsecutiveFailures(0)
  }, [])

  const recordFailure = useCallback(() => {
    setConsecutiveFailures((prev) => prev + 1)
  }, [])

  const reset = useCallback(() => {
    setConsecutiveFailures(0)
  }, [])

  return {
    consecutiveFailures,
    shouldStopPolling,
    recordSuccess,
    recordFailure,
    reset,
  }
}

export default useConsecutiveFailures
